import { useState, useEffect, useCallback, useRef } from 'react';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_PING_INTERVAL = 25000; // 25 seconds (less than server timeout)

export function usePriceWebSocket(enabled = true) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connected', 'disconnected', 'connecting'
  const [priceData, setPriceData] = useState(null);
  const [recentSwaps, setRecentSwaps] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const enabledRef = useRef(enabled);
  
  // Keep enabledRef in sync
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const connect = useCallback(() => {
    if (!enabledRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Determine WebSocket URL based on environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL 
      ? new URL(process.env.REACT_APP_BACKEND_URL).host 
      : window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/prices`;
    
    setConnectionStatus('connecting');
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setConnectionStatus('connected');
        console.log('[WebSocket] Connected to price feed');
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, WS_PING_INTERVAL);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'price_update') {
            setPriceData(data);
            setLastUpdate(new Date());
            
            // Update recent swaps if included
            if (data.recent_swaps && Array.isArray(data.recent_swaps)) {
              setRecentSwaps(data.recent_swaps);
            }
          }
          // Ignore pong messages
        } catch (e) {
          console.error('[WebSocket] Failed to parse message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        setConnectionStatus('disconnected');
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Auto-reconnect if still enabled
        if (enabledRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WebSocket] Attempting reconnect...');
            connect();
          }, WS_RECONNECT_DELAY);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };
      
    } catch (error) {
      console.error('[WebSocket] Failed to connect:', error);
      setConnectionStatus('disconnected');
    }
  }, []);

  const disconnect = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnectionStatus('disconnected');
  }, []);

  // Request immediate refresh
  const requestRefresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('refresh');
    }
  }, []);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    connectionStatus,
    priceData,
    recentSwaps,
    lastUpdate,
    requestRefresh,
    isConnected: connectionStatus === 'connected',
  };
}
